'use client';

import { COLORS } from '@/design-tokens';
import { FEATURE_FLAGS } from './feature-flags';
import { isLocalDevelopment } from '@/utils/is-local-host';
import { TAppScoringFormat, TUserRole } from '@/types';
import moment from 'moment';

const CURRENT_YEAR = moment().format('YYYY');

/** Version controller for the UI */
export const APP_UI_VERSION = '1.0.9' as const;
/** Version controller for the API */
export const APP_API_VERSION = '1.0.2' as const;

/**
 * Last CAC approval date used throughout the app.
 */
export const APP_PLATFORM_APPROVAL_DATE = '05/26' as const;

/**
 * Client name used throughout the app.
 */
export const CLIENT_NAME = 'Johnson & Johnson' as const;

/**
 * Client name abbreviation used throughout the app.
 */
export const CLIENT_NAME_ABBR = 'J&J' as const;

/**
 * Application name used throughout the app.
 */
export const APP_NAME = 'FieldOne' as const;

/**
 * Application description used throughout the app.
 */
export const APP_DESCRIPTION =
  `${APP_NAME} is a comprehensive platform designed to streamline and enhance operations for ${CLIENT_NAME}.` as const;

/**
 * Application version used in the disclaimer.
 */
export const APP_VERSION = 'em-194456v4' as const;

/**
 * Application version used in the disclaimer for manager-facing app views.
 */
export const MANAGER_APP_VERSION = 'em-194456v4' as const;

/**
 * Application disclaimer used throughout the app.
 */
export const APP_DISCLAIMER =
  `This material is being provided for INTERNAL EDUCATIONAL AND BACKGROUND PURPOSES ONLY and is NOT FOR PROMOTIONAL USE. Do not copy, distribute, or share with physicians, staff, or patients. © ${CLIENT_NAME} and its affiliates 2026 ${APP_PLATFORM_APPROVAL_DATE} ${APP_VERSION}` as const;

/**
 * Application disclaimer used in the manager-facing app views.
 */
export const MANAGER_APP_DISCLAIMER = APP_DISCLAIMER.replace(APP_VERSION, MANAGER_APP_VERSION);

/**
 * Application disclaimer used throughout the app.
 */
export const APP_WARNING_NOTICE =
  `WARNING NOTICE: This system is restricted solely to ${CLIENT_NAME} users for legitimate business only. The actual or attempted unauthorized access, use or modification of this system is strictly prohibited by ${CLIENT_NAME}. Unauthorized users are subject to Company disciplinary proceedings and/or criminal and civil penalties under state, federal or other applicable domestic and foreign laws. The use of this system may be monitored and recorded for administrative and security reasons. If such monitoring and/or recording reveal possible evidence of criminal activity, ${CLIENT_NAME} may provide the evidence of such monitoring to law enforcement officials. © Information Technology Shared Services, a Division of ${CLIENT_NAME} Services, Inc. ${CURRENT_YEAR}` as const;

/**
 * External links to J&J privacy-related pages.
 */
export const APP_EXTERNAL_LINKS = [
  {
    labelKey: 'APP_PRIVACY_POLICY',
    label: 'Privacy Policy',
    href: 'https://www.jnj.com/privacy-policy',
  },
  {
    labelKey: 'APP_LEGAL_NOTICE',
    label: 'Legal Notice',
    href: 'https://www.jnj.com/legal-notice',
  },
  {
    labelKey: 'APP_HELP_SUPPORT',
    label: 'Help & Support',
    href: 'https://jnjprod.service-now.com/iris?id=esc_nas_contact_page',
  },
] as const;

/**
 * Links to display in the app header for quick access to important resources. Each link
 * is be associated with a parent category (e.g., 'support', 'profile') to group related
 * links together in the UI.
 */
export const APP_HEADER_LINKS: {
  labelKey: string;
  label: string;
  href: string;
  icon?: string;
  external: boolean;
  parent: 'support' | 'profile';
  featureFlag: keyof typeof FEATURE_FLAGS | null;
}[] = [
  {
    labelKey: 'APP_SUPPORT_RESOURCES',
    label: 'FieldOne Support Resources',
    href: 'https://www.janssenlearn.com/link/lesson/f9e8891a-adaf-4d67-8c1a-7f818896aa4d',
    icon: 'ArrowSquareOut',
    external: true,
    parent: 'support',
    featureFlag: null,
  },
  {
    labelKey: 'APP_HELP_SUPPORT',
    label: 'Help & Support',
    href: 'https://jnjprod.service-now.com/iris?id=esc_nas_contact_page',
    icon: 'ArrowSquareOut',
    external: true,
    parent: 'support',
    featureFlag: null,
  },
  {
    labelKey: 'MY_PROFILE',
    label: 'My Profile',
    href: '/profile',
    external: false,
    parent: 'profile',
    featureFlag: 'ENABLE_USER_PROFILE_PAGE',
  },
];

/**
 * Application scoring format used throughout the app.
 *
 * Options:
 * - `numerical` - Scores are displayed as raw numerical values.
 * - `numerical clamp` - Scores are calculated but clamped within a specific range.
 * - `numerical grade` - Scores are displayed as "X out of Y" or "X/Y".
 * - `numerical round` - Scores are rounded to the nearest whole number.
 * - `percentage` - Scores are displayed as a percentage with zero decimal places.
 */
export const APP_SCORING_FORMAT: TAppScoringFormat = FEATURE_FLAGS.ENABLE_SCORECARD_V2
  ? ('percentage' as const)
  : ('numerical grade' as const);

/**
 * Maximum score value used in the app when the scoring format is set to "numerical grade".
 */
export const APP_SCORING_MAX_SCORE = APP_SCORING_FORMAT === 'percentage' ? 100 : (4 as const);

/**
 * Score colors used throughout the app based on skill levels.
 */
export const APP_SCORE_COLORS = {
  L1: FEATURE_FLAGS.ENABLE_SCORECARD_V2
    ? COLORS.semantic.score.l1
    : COLORS.semantic.score['legacy-l1'],
  L2: FEATURE_FLAGS.ENABLE_SCORECARD_V2
    ? COLORS.semantic.score.l2
    : COLORS.semantic.score['legacy-l2'],
  L3: FEATURE_FLAGS.ENABLE_SCORECARD_V2
    ? COLORS.semantic.score.l3
    : COLORS.semantic.score['legacy-l3'],
  L4: FEATURE_FLAGS.ENABLE_SCORECARD_V2
    ? COLORS.semantic.score.l4
    : COLORS.semantic.score['legacy-l4'],
};

/**
 * Score percentage ranges corresponding to skill levels when the scoring
 * format is set to "percentage". For example, a score of 85% would fall into
 * the L3 category based on these ranges.
 */
export const APP_SCORING_PERCENTAGE_RANGES = {
  L1: [0, 20],
  L2: [21, 50],
  L3: [51, 89],
  L4: [90, 100],
};

/**
 * Flag to enable or disable sticky <PageHeader /> across the app.
 */
export const APP_PAGE_HEADER_STICKY = false as const;

/**
 * Array of user roles that have access to the manager dashboard and related views.
 */
export const APP_MANAGER_ACCESS_ROLES = [
  'developer',
  'dm',
  'maintainer',
  'nsd',
  'rbd',
] as TUserRole[];

/**
 * Array of user roles that have access to the developer dashboard and related views.
 */
export const APP_DEVELOPER_ACCESS_ROLES = ['developer'] as TUserRole[];

/**
 * Array of user roles that have access to maintainer-only tooling, such as
 * the Voice Live runtime inspector.
 */
export const APP_MAINTAINER_ACCESS_ROLES = ['maintainer'] as TUserRole[];

/**
 * Flag to enable or disable the use of mock data in development environment(s).
 */
export const USE_MOCK_DATA = process.env.NEXT_PUBLIC_DATA_ENV === 'mock' && isLocalDevelopment();

/**
 * Default issuing team name used in the certificate generation process when no team is specified.
 */
export const DEFAULT_ISSUING_TEAM = 'Immunology (Dermatology) Learning Excellence Team' as const;

/**
 * Default certification status used in the manager dashboard when no status is provided.
 */
export const DEFAULT_CERTIFICATION_STATUS = 'not-started' as const;

/**
 * Configuration object for styles that can be easily imported and applied across components.
 * This promotes consistency and makes it easier to update styles globally from a single location.
 */
export const APP_STYLE_CONFIG = {
  /** Whether cards should have a border */
  showCardBorder: false,
  /** Whether card's inner content should have a border */
  showCardContentBorder: true,
  /** Whether to animate numbers in the UI */
  useAnimatedNumbers: true,
  /** Whether to animate height transitions in the UI */
  useAnimatedHeightTransition: false,
  /** Whether to animate roleplay cards in the UI */
  useAnimatedRoleplayCards: true,
  /** Whether to animate table row changes in the UI */
  useAnimatedTables: true,
  /** Whether to animate simulation preparation progress in the UI */
  useAnimatedPrepProgress: true,
};

/**
 * Centralized configuration object for application constants.
 */
export const CONFIG = {
  APP_API_VERSION,
  APP_DESCRIPTION,
  APP_DEVELOPER_ACCESS_ROLES,
  APP_DISCLAIMER,
  APP_EXTERNAL_LINKS,
  APP_HEADER_LINKS,
  APP_MAINTAINER_ACCESS_ROLES,
  APP_MANAGER_ACCESS_ROLES,
  APP_NAME,
  APP_PAGE_HEADER_STICKY,
  APP_SCORING_FORMAT,
  APP_SCORING_MAX_SCORE,
  APP_SCORING_PERCENTAGE_RANGES,
  APP_STYLE_CONFIG,
  APP_UI_VERSION,
  APP_VERSION,
  APP_WARNING_NOTICE,
  CLIENT_NAME_ABBR,
  CLIENT_NAME,
  DEFAULT_CERTIFICATION_STATUS,
  DEFAULT_ISSUING_TEAM,
  USE_MOCK_DATA,
};
